/**
 * Reading the charts integration's state: what the chart plots, and whether it can.
 */

/** One rendered dataset — a dimension or a value — as the integration publishes it. */
interface ChartDatasetSlice {
    id?: string;
    label?: string;
    data?: unknown[];
}

/** The part of a chart's state this module reads. */
interface ChartStateSlice {
    type?: string;
    dimensions?: ChartDatasetSlice[];
    values?: ChartDatasetSlice[];
}

/** A dimension entry as the integration stores it in grid state. */
interface ChartDimensionSelection {
    field: string;
    hidden?: boolean;
}

// ─── Is there a chart? ───────────────────────────────────────────────────────

/**
 * Whether the pane has something worth showing.
 *
 * A chart needs a type and both sections. The integration blanks both as soon as either
 * empties, so clearing the fields dismisses a chart without disturbing its type.
 */
export function hasConfiguredChart(chartState: ChartStateSlice | undefined): boolean {
    return (
        Boolean(chartState?.type) &&
        (chartState?.dimensions?.length ?? 0) > 0 &&
        (chartState?.values?.length ?? 0) > 0
    );
}

// ─── Describing a chart ──────────────────────────────────────────────────────

/** The labels of one section, skipping any the integration left unnamed. */
function labelsOf(slices: ChartDatasetSlice[] | undefined): string[] {
    return (slices ?? [])
        .map((slice) => slice.label)
        .filter((label): label is string => Boolean(label));
}

/** "A", "A and B", "A, B and C". */
function joinLabels(labels: string[]): string {
    if (labels.length < 2) return labels[0] ?? '';
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * What the chart plots, as a heading — "Acres by Region".
 *
 * Built from the labels the integration publishes, so it reads the same as the charts panel
 * and the column headers. Undefined when there is no chart yet; the caller names the empty
 * pane.
 */
export function describeChart(chartState: ChartStateSlice | undefined): string | undefined {
    if (!hasConfiguredChart(chartState)) return undefined;

    const values = labelsOf(chartState?.values);
    const dimensions = labelsOf(chartState?.dimensions);
    if (values.length === 0 || dimensions.length === 0) return undefined;

    return `${joinLabels(values)} by ${joinLabels(dimensions)}`;
}

// ─── Cardinality ─────────────────────────────────────────────────────────────

/**
 * How many categories each chart type can draw before the DOM becomes the bottleneck.
 *
 * The renderer emits an SVG element per category per series, so cost tracks node count.
 * Pie is worst: it draws an arc *and* a legend entry for every slice.
 */
const CHART_CATEGORY_LIMITS: Record<string, number> = {
    pie: 50,
    column: 150,
    bar: 150,
    line: 400,
    area: 400,
};

const DEFAULT_CATEGORY_LIMIT = 150;

/** The ceiling for a chart type, falling back for one we don't recognise. */
export function getCategoryLimit(type: string | undefined): number {
    return CHART_CATEGORY_LIMITS[type ?? ''] ?? DEFAULT_CATEGORY_LIMIT;
}

/**
 * How many categories the chart will plot.
 *
 * The renderer takes its category axis from the first dimension whatever the chart type, so
 * that one length is the one that matters.
 */
export function getCategoryCount(chartState: ChartStateSlice | undefined): number {
    return chartState?.dimensions?.[0]?.data?.length ?? 0;
}

/** Whether this chart would exceed its type's ceiling. */
export function exceedsCategoryLimit(chartState: ChartStateSlice | undefined): boolean {
    return getCategoryCount(chartState) > getCategoryLimit(chartState?.type);
}

/**
 * A key identifying the current selection: type, dimensions and category count.
 *
 * Scopes the user's "draw it anyway" override. Any of these changing — a different field, or
 * the same one grown under a looser filter — invalidates it and asks again.
 */
export function getCardinalityOverrideKey(chartState: ChartStateSlice | undefined): string {
    const dimensions = (chartState?.dimensions ?? []).map((dimension) => dimension.id).join(',');
    return `${chartState?.type ?? ''}|${dimensions}|${getCategoryCount(chartState)}`;
}

// ─── Dimensions to row grouping ──────────────────────────────────────────────

/**
 * The row grouping a chart's dimensions require.
 *
 * The integration reads rows at depth `dimensions.length - 1`. On an ungrouped grid that is
 * the flat leaf rows, so a pie of Type would draw a slice per record. Grouping by the
 * dimensions turns that depth into group rows, which is what gives the chart its aggregate.
 *
 * Hidden dimensions are left out, and the list is clamped to `maxDimensions` where the chart
 * type sets one — a pie takes only its first.
 */
export function getRequiredGrouping(
    dimensions: readonly ChartDimensionSelection[] | undefined,
    maxDimensions?: number
): string[] {
    const visible = (dimensions ?? [])
        .filter((dimension) => dimension.hidden !== true)
        .map((dimension) => dimension.field);

    return maxDimensions === undefined ? visible : visible.slice(0, maxDimensions);
}

/**
 * Whether `active` already covers what `required` needs.
 *
 * A prefix match, not equality: extra grouping levels *below* the chart's dimensions leave
 * the depth it reads untouched, so grouping by [Type, Status] serves a chart needing [Type].
 */
export function isGroupingSufficient(
    required: readonly string[],
    active: readonly string[]
): boolean {
    return required.every((field, index) => active[index] === field);
}
